import { COLORS, LIFT_PARTS } from '../theme.js';
import type { RoomKind } from './homeModel.js';

/**
 * The furnished rooms, one per kind (ADR-0018 places, ADR-0019 home).
 *
 * Drawn once, as SVG on a 240×96 box with the floor at y 94: the home screen
 * shows them now, and the game's own floors will draw the same rooms when each
 * building is furnished, so nothing is drawn twice. The left of the box is kept
 * clear — that is where people wait, beside the shaft — except for what marks a
 * building's rule: the hotel's three rugs sit exactly where three guests stand.
 *
 * No floor hue anywhere: these share the shaft with the tickets.
 *
 * A few parts move in play (`ROOM_LIFE`): leaves sway, the lobby clock keeps
 * the shift's time, screens glow, the train comes and goes. Each is drawn here
 * once, as its own small picture — in the still drawing where it rests, left
 * out of the play texture and moved on top of it instead.
 */
const css = (colour: number): string => `#${colour.toString(16).padStart(6, '0')}`;
const INK = css(COLORS.ink);
const BRASS = css(LIFT_PARTS.brass);
const IRON = css(LIFT_PARTS.iron);
const STEEL = css(LIFT_PARTS.steel);
const STEEL_DARK = css(LIFT_PARTS.steelDark);
const GLOW = css(LIFT_PARTS.glow);
const GLASS = css(LIFT_PARTS.glass);
const CREAM = css(COLORS.cabin);
const WOOD = '#c98f5c';
// Darker than it first was: #a36a3c sat 18.3 from floor 2's amber, inside the 18.7 bar.
const WOOD_TOP = '#94704f';
const LEAF = '#7fa874';
const RUG = '#c98a8e';
const SKY = '#d6e6ee';

const line = `stroke="${INK}" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"`;

function glow(cx: number, cy: number, r: number): string {
  return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#room-glow)"/>`;
}

interface Point {
  readonly x: number;
  readonly y: number;
}

/** What a moving part does in play (view/roomLife.ts). */
export type Motion = 'sway' | 'hour' | 'minute' | 'glow' | 'train';

/** A part of a room that moves in play, drawn on its own. All in room units. */
export interface LivePart {
  readonly motion: Motion;
  /** Its drawing, in the room's own coordinates. */
  readonly svg: string;
  /** The box its drawing fits in: what is rasterised. */
  readonly box: Point & { readonly width: number; readonly height: number };
  /** What it turns about: a stem's foot, a clock's centre. */
  readonly pivot: Point;
  /** How far it is turned in the still drawing, in degrees. */
  readonly rest: number;
}

/**
 * How a room draws its moving parts: into the still drawing, or not at all
 * (play draws them itself), or into a list (`ROOM_LIFE`).
 */
type Live = (part: LivePart) => string;

/**
 * The still drawing: each part where it rests. A glow is only ever drawn live.
 * Classed by motion, so the home screen can sway its leaves too.
 */
const still: Live = (part) => {
  if (part.motion === 'glow') return '';
  const { x, y } = part.pivot;
  return `<g transform="rotate(${part.rest} ${x} ${y})"><g class="room-${part.motion}" style="transform-origin: ${x}px ${y}px">${part.svg}</g></g>`;
};

function plant(x: number, live: Live, scale = 1): string {
  const s = scale;
  return `<g transform="translate(${x} 94) scale(${s})">
    <path d="M-7 0 l2 -12 h10 l2 12 z" fill="#c9774f" ${line}/>
  </g>${live(leaves(x, s))}`;
}

/** A plant's stem and leaves, swaying from the top of its pot. */
function leaves(x: number, s: number): LivePart {
  return {
    motion: 'sway',
    svg: `<g transform="translate(${x} 94) scale(${s})">
    <path d="M0 -12 v-10" ${line} fill="none"/>
    <path d="M0 -20 q-11 -3 -13 6 q6 -3 13 -6 M0 -20 q11 -3 13 6 q-6 -3 -13 -6 M0 -22 q-5 -9 -12 -7 q5 2 12 7 M0 -22 q5 -9 12 -7 q-5 2 -12 7" fill="${LEAF}" ${line}/>
  </g>`,
    box: { x: x - 15 * s, y: 94 - 33 * s, width: 30 * s, height: 23 * s },
    pivot: { x, y: 94 - 12 * s },
    rest: 0,
  };
}

/** A clock hand pointing at twelve, `length` long; `rest` is where the still drawing has it. */
function hand(
  motion: 'hour' | 'minute',
  cx: number,
  cy: number,
  length: number,
  rest: number,
): LivePart {
  return {
    motion,
    svg: `<path d="M${cx} ${cy} v-${length}" ${line}/>`,
    box: { x: cx - 2, y: cy - length - 1.5, width: 4, height: length + 3 },
    pivot: { x: cx, y: cy },
    rest,
  };
}

/** A soft light over a screen. */
function screenGlow(x: number, y: number, width: number, height: number): LivePart {
  return {
    motion: 'glow',
    svg: `<rect x="${x}" y="${y}" width="${width}" height="${height}" rx="1" fill="${CREAM}"/>`,
    box: { x, y, width, height },
    pivot: { x, y },
    rest: 0,
  };
}

function door(x: number): string {
  return `<rect x="${x}" y="48" width="22" height="46" rx="2" fill="${WOOD_TOP}" ${line}/>
    <circle cx="${x + 17}" cy="72" r="1.6" fill="${BRASS}"/>`;
}

/** The hotel's rule as furniture: three rugs where three guests may stand. */
const RUGS = `<ellipse cx="22" cy="93" rx="17" ry="3.2" fill="${RUG}"/>
  <ellipse cx="58" cy="93" rx="17" ry="3.2" fill="${RUG}"/>
  <ellipse cx="94" cy="93" rx="17" ry="3.2" fill="${RUG}"/>`;

function painting(x: number, y: number): string {
  return `<rect x="${x}" y="${y}" width="28" height="19" rx="1" fill="#e2cfa8" ${line}/>
    <path d="M${x + 3} ${y + 15} q6 -8 11 -2 q4 -6 11 2" fill="none" ${line} opacity="0.6"/>`;
}

function deskWithMonitor(x: number, live: Live): string {
  return `<rect x="${x}" y="70" width="52" height="5" rx="1.5" fill="${WOOD}" ${line}/>
    <path d="M${x + 4} 75 V94 M${x + 48} 75 V94" ${line}/>
    <rect x="${x + 14}" y="52" width="24" height="16" rx="2" fill="${IRON}" ${line}/>
    <rect x="${x + 17}" y="55" width="18" height="10" rx="1" fill="${GLASS}"/>
    ${live(screenGlow(x + 17, 55, 18, 10))}
    <path d="M${x + 26} 68 v2" ${line}/>
    <path d="M${x + 30} 94 v-12 h10 v-8" fill="none" ${line}/>`;
}

function seat(x: number): string {
  return `<path d="M${x} 94 v-12 h12 v12 M${x} 82 v-10" fill="none" ${line}/>`;
}

/** Where the platform's train sits, and the middle of its door: arrivals step out there. */
const TRAIN_X = 158;
export const TRAIN_DOOR_X = TRAIN_X + 44;

/** The train at the platform, running off past the right-hand wall. */
function trainCar(x: number): LivePart {
  const width = 240 - x + 20;
  return {
    motion: 'train',
    svg: `<rect x="${x}" y="40" width="${width}" height="46" rx="10" fill="${STEEL}" ${line}/>
    <rect x="${x}" y="70" width="${width}" height="5" fill="${BRASS}"/>
    <rect x="${x + 8}" y="48" width="20" height="16" rx="3" fill="${GLASS}" ${line}/>
    <rect x="${x + 34}" y="45" width="20" height="39" rx="2" fill="${STEEL_DARK}" ${line}/>
    <rect x="${x + 38}" y="49" width="12" height="13" rx="2" fill="${GLASS}" ${line}/>
    <rect x="${x + 60}" y="48" width="20" height="16" rx="3" fill="${GLASS}" ${line}/>
    <circle cx="${x + 16}" cy="88" r="4" fill="${IRON}"/><circle cx="${x + 72}" cy="88" r="4" fill="${IRON}"/>`,
    box: { x: x - 1, y: 39, width: width + 2, height: 54 },
    pivot: { x, y: 40 },
    rest: 0,
  };
}

function skyWindow(x: number, w: number, skyline: boolean): string {
  const city = skyline
    ? `<path d="M${x} 66 h10 v-12 h8 v6 h10 v-16 h9 v22 h12 v-8 h10 v8 h${w - 59} v10 H${x} z" fill="#b9c6cf"/>`
    : '';
  return `<rect x="${x}" y="30" width="${w}" height="46" rx="2" fill="${SKY}" ${line}/>
    ${city}
    <path d="M${x + w / 2} 30 V76 M${x} 53 H${x + w}" stroke="${CREAM}" stroke-width="2.5"/>
    <rect x="${x}" y="30" width="${w}" height="46" rx="2" fill="none" ${line}/>`;
}

const ROOMS: Readonly<Record<RoomKind, (live: Live) => string>> = {
  'hotel-reception': (live) => `${glow(150, 18, 34)}
    <path d="M150 0 v8" ${line} opacity="0.6"/>
    <path d="M138 12 h24 M141 12 q9 9 18 0" fill="none" ${line} opacity="0.7"/>
    <circle cx="150" cy="14" r="3.5" fill="${GLOW}" ${line}/>
    <rect x="178" y="30" width="44" height="16" rx="2" fill="#efe3cc" ${line}/>
    <circle cx="186" cy="36" r="1.6" fill="${BRASS}"/><circle cx="195" cy="36" r="1.6" fill="${BRASS}"/><circle cx="204" cy="36" r="1.6" fill="${BRASS}"/><circle cx="213" cy="36" r="1.6" fill="${BRASS}"/>
    <circle cx="186" cy="41" r="1.6" fill="${BRASS}"/><circle cx="195" cy="41" r="1.6" fill="${BRASS}"/><circle cx="204" cy="41" r="1.6" fill="${BRASS}"/><circle cx="213" cy="41" r="1.6" fill="${BRASS}"/>
    <rect x="166" y="64" width="70" height="6" rx="2" fill="${WOOD_TOP}" ${line}/>
    <rect x="170" y="70" width="62" height="24" fill="${WOOD}" ${line}/>
    <path d="M222 64 a5 5 0 0 1 10 0 z" fill="${BRASS}" ${line}/>
    ${plant(124, live)}`,

  'office-lobby': (live) => `${glow(170, 20, 40)}
    <circle cx="146" cy="34" r="9" fill="${CREAM}" ${line}/>
    ${live(hand('hour', 146, 34, 4.5, 270))}${live(hand('minute', 146, 34, 7, 0))}
    ${plant(128, live)}
    <rect x="152" y="66" width="80" height="6" rx="2" fill="${WOOD_TOP}" ${line}/>
    <rect x="156" y="72" width="72" height="22" fill="#d9cbb4" ${line}/>
    <text x="192" y="87" text-anchor="middle" font-family="system-ui, sans-serif" font-size="7" font-weight="800" letter-spacing="1" fill="${INK}" opacity="0.7">RECEPTION</text>`,

  'office-desks': (live) => `${glow(150, 14, 34)}${glow(210, 14, 34)}
    ${deskWithMonitor(118, live)}${deskWithMonitor(180, live)}`,

  'office-meeting': () => `${glow(190, 14, 38)}
    <rect x="114" y="34" width="56" height="30" rx="2" fill="${CREAM}" ${line}/>
    <path d="M120 42 h24 M120 49 h34 M120 56 q8 -6 14 0 t14 0" fill="none" stroke="${STEEL_DARK}" stroke-width="1.8" stroke-linecap="round"/>
    <rect x="164" y="68" width="70" height="6" rx="2" fill="${WOOD}" ${line}/>
    <path d="M172 74 V94 M226 74 V94" ${line}/>
    ${seat(176)}${seat(208)}`,

  'office-cafeteria': () => `${glow(140, 14, 34)}
    <rect x="114" y="36" width="40" height="22" rx="2" fill="${IRON}" ${line}/>
    <path d="M119 43 h20 M119 49 h28" stroke="${GLOW}" stroke-width="2" stroke-linecap="round"/>
    <rect x="178" y="62" width="58" height="32" fill="#d9cbb4" ${line}/>
    <rect x="176" y="58" width="62" height="5" rx="2" fill="${WOOD_TOP}" ${line}/>
    <rect x="198" y="40" width="18" height="18" rx="2" fill="${STEEL}" ${line}/>
    <circle cx="207" cy="49" r="3" fill="${IRON}"/>
    <rect x="120" y="74" width="30" height="4" rx="2" fill="${WOOD}" ${line}/>
    <path d="M135 78 V94 M128 94 h14" ${line}/>`,

  'office-executive': (live) => `${glow(200, 16, 40)}
 ${painting(122, 38)}
    <ellipse cx="190" cy="93" rx="42" ry="3.5" fill="#b8a07a"/>
    <rect x="160" y="64" width="72" height="8" rx="2" fill="${WOOD_TOP}" ${line}/>
    <rect x="164" y="72" width="64" height="22" fill="${WOOD}" ${line}/>
    <path d="M196 72 v22" ${line} opacity="0.5"/>
    <path d="M175 64 v-26 a10 10 0 0 1 20 0 v26" fill="#6e5a4a" ${line}/>
    ${plant(236, live, 1.1)}`,

  'hotel-corridor': () => `@@RUGS@@${glow(150, 30, 26)}${glow(215, 30, 26)}
    ${painting(168, 36)}${door(126)}${door(206)}`,

  'hotel-luggage': () => `@@RUGS@@${glow(190, 24, 32)}
    <path d="M128 92 V58 Q144 46 160 58 V92" fill="none" stroke="${BRASS}" stroke-width="3"/>
    <rect x="124" y="90" width="40" height="4" rx="1" fill="${BRASS}"/>
    <rect x="132" y="68" width="14" height="22" rx="2" fill="#8c7a68" ${line}/>
    <rect x="146" y="76" width="12" height="14" rx="2" fill="#b3a48f" ${line}/>
    <rect x="176" y="78" width="32" height="16" rx="2" fill="#6e5a4a" ${line}/>
    <rect x="182" y="64" width="22" height="14" rx="2" fill="#b3a48f" ${line}/>
    <path d="M226 46 V92 M218 93 h16" ${line}/>
    <path d="M220 54 q-4 14 -1 26 h12 q3 -12 -1 -26 z" fill="#8c7a68" ${line}/>`,

  'hotel-housekeeping': () => `@@RUGS@@${glow(170, 26, 30)}
    ${door(124)}
    <path d="M160 60 h6 v4" fill="none" ${line}/>
    <rect x="164" y="64" width="46" height="26" rx="2" fill="#efe3cc" ${line}/>
    <path d="M164 77 h46" ${line}/>
    <rect x="168" y="67" width="12" height="7" rx="1" fill="#9fd3c7" ${line}/>
    <rect x="182" y="67" width="12" height="7" rx="1" fill="#f2c6c2" ${line}/>
    <rect x="168" y="80" width="16" height="7" rx="1" fill="${CREAM}" ${line}/>
    <circle cx="170" cy="92" r="2.5" fill="${INK}"/><circle cx="204" cy="92" r="2.5" fill="${INK}"/>
    ${door(214)}`,

  'hotel-suite': () => `@@RUGS@@${glow(140, 50, 22)}
    ${skyWindow(168, 60, false)}
    <path d="M166 14 q6 30 0 62 M230 14 q-6 30 0 62" fill="#c98a8e" opacity="0.5"/>
    <path d="M140 94 v-28 M132 66 h16 l-3 -10 h-10 z" fill="${GLOW}" ${line}/>
    <rect x="170" y="78" width="56" height="12" rx="4" fill="#a0806a" ${line}/>
    <rect x="166" y="70" width="10" height="20" rx="3" fill="#a0806a" ${line}/>
    <rect x="220" y="70" width="10" height="20" rx="3" fill="#a0806a" ${line}/>`,

  // Breakfast at the top of the hotel (ADR-0051): the buffet, its coffee urn,
  // and a laid table by the window. The board over it says when it closes.
  'hotel-restaurant': () => `@@RUGS@@${glow(150, 22, 30)}
    ${skyWindow(182, 52, false)}
    <rect x="124" y="62" width="44" height="6" rx="2" fill="${WOOD_TOP}" ${line}/>
    <rect x="127" y="68" width="38" height="26" fill="${WOOD}" ${line}/>
    <rect x="130" y="48" width="11" height="14" rx="2" fill="${STEEL}" ${line}/>
    <path d="M135.5 48 v-3 M141 56 h3" ${line}/>
    <path d="M147 62 q8 -10 16 0 z" fill="${STEEL}" ${line}/>
    <circle cx="155" cy="51.5" r="1.4" fill="${STEEL}" ${line}/>
    <rect x="180" y="78" width="44" height="5" rx="2" fill="#efe3cc" ${line}/>
    <path d="M185 83 l-2 11 M219 83 l2 11" ${line}/>
    <path d="M198 77 v-4 h6 v4" fill="${CREAM}" ${line}/>
    ${seat(170)}${seat(228)}`,

  'transit-platform': (live) => `<rect x="0" y="88" width="240" height="6" fill="${GLOW}"/>
    <path d="M6 88 l6 6 M26 88 l6 6 M46 88 l6 6 M66 88 l6 6 M86 88 l6 6 M106 88 l6 6" stroke="${INK}" stroke-width="2"/>
    <rect x="108" y="36" width="42" height="12" rx="2" fill="${IRON}" ${line}/>
    <text x="129" y="45" text-anchor="middle" font-family="system-ui, sans-serif" font-size="7" font-weight="800" fill="${GLOW}">PLATFORM</text>
    <path d="M129 48 v12" ${line}/>
    ${live(trainCar(TRAIN_X))}`,

  'transit-concourse': (live) => `${glow(160, 14, 40)}
    <rect x="130" y="34" width="84" height="26" rx="3" fill="${IRON}" ${line}/>
    <path d="M136 42 h40 M136 49 h52 M136 55 h30 M196 42 h12 M196 49 h12 M196 55 h12" stroke="${GLOW}" stroke-width="2" stroke-linecap="round"/>
    <path d="M162 60 v8 M182 60 v8" ${line}/>
    <rect x="150" y="80" width="56" height="6" rx="2" fill="${WOOD}" ${line}/>
    <path d="M156 86 V94 M200 86 V94" ${line}/>
    ${plant(226, live)}`,

  'transit-tickets': (live) => `${glow(190, 14, 36)}
    <rect x="160" y="44" width="28" height="50" rx="4" fill="${STEEL}" ${line}/>
    <rect x="165" y="50" width="18" height="12" rx="2" fill="${GLASS}" ${line}/>
    ${live(screenGlow(166, 51, 16, 10))}
    <rect x="170" y="70" width="8" height="3" fill="${IRON}"/>
    <rect x="200" y="44" width="28" height="50" rx="4" fill="${STEEL}" ${line}/>
    <rect x="205" y="50" width="18" height="12" rx="2" fill="${GLASS}" ${line}/>
    ${live(screenGlow(206, 51, 16, 10))}
    <rect x="210" y="70" width="8" height="3" fill="${IRON}"/>
    <path d="M118 94 v-26 M144 94 v-26" stroke="${BRASS}" stroke-width="3"/>
    <path d="M118 72 q13 8 26 0" fill="none" stroke="#a0806a" stroke-width="2.5"/>`,

  'skyline-skylobby': (live) => `${skyWindow(150, 86, true)}
    <rect x="110" y="38" width="36" height="16" rx="3" fill="${BRASS}" ${line}/>
    <text x="128" y="49" text-anchor="middle" font-family="system-ui, sans-serif" font-size="6.5" font-weight="800" fill="${INK}">CHANGE</text>
    <path d="M128 54 v10" ${line}/>
    ${plant(132, live)}`,

  'skyline-office': (live) => `${skyWindow(186, 50, true)}
    ${deskWithMonitor(122, live)}`,

  'skyline-view': (live) => `${skyWindow(118, 118, true)}
    <path d="M150 94 l10 -24 l10 24 M160 70 l14 -10" fill="none" ${line}/>
    <rect x="170" y="54" width="18" height="8" rx="3" transform="rotate(-35 179 58)" fill="${IRON}" ${line}/>
    ${plant(226, live)}`,
};

const DEFS =
  '<defs><radialGradient id="room-glow"><stop offset="0" stop-color="#ffe2a6" stop-opacity="0.85"/><stop offset="1" stop-color="#ffe2a6" stop-opacity="0"/></radialGradient></defs>';

export const ROOM_KINDS = Object.keys(ROOMS) as RoomKind[];

/** Each room's moving parts, in the order they are drawn. */
export const ROOM_LIFE: Readonly<Record<RoomKind, readonly LivePart[]>> = (() => {
  const life = {} as Record<RoomKind, readonly LivePart[]>;
  for (const kind of ROOM_KINDS) {
    const parts: LivePart[] = [];
    ROOMS[kind]((part) => {
      parts.push(part);
      return '';
    });
    life[kind] = parts;
  }
  return life;
})();

export interface RoomOptions {
  /** The hotel's rugs. Play draws them where the waiting row actually stands. */
  readonly rugs: boolean;
  /** The moving parts, where they rest. Play draws them itself, moving. */
  readonly live: boolean;
}

/**
 * A room's drawing, inner markup only. `rugs: false` leaves the hotel's rugs
 * out: in play they are drawn where the waiting row actually stands, which a
 * scaled picture cannot promise. `live: false` leaves the moving parts out.
 */
export function roomInner(
  kind: RoomKind,
  options: RoomOptions = { rugs: true, live: true },
): string {
  const live: Live = options.live ? still : () => '';
  return DEFS + ROOMS[kind](live).replace('@@RUGS@@', options.rugs ? RUGS : '');
}

/** A room as a standalone SVG document, for rasterising into a texture. */
export function roomDocument(kind: RoomKind, options: RoomOptions): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 96" width="240" height="96">${roomInner(kind, options)}</svg>`;
}

/** One moving part as a standalone SVG document, cropped to its own box. */
export function partDocument(part: LivePart): string {
  const { x, y, width, height } = part.box;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${x} ${y} ${width} ${height}" width="${width}" height="${height}">${part.svg}</svg>`;
}

/** A room as an SVG element, aligned to the floor it stands on. */
export function roomSvg(kind: RoomKind): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 240 96');
  svg.setAttribute('preserveAspectRatio', 'xMinYMax meet');
  svg.setAttribute('class', 'room-art');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = roomInner(kind);
  return svg;
}
